import type { TrackingDriver } from "@/tracking/contracts/TrackingDriver"
import type { Axios } from "axios"
import type { TrackingEvent, TrackingEventPayload } from "@/tracking/types/TrackingEvent.ts"
import { Capacitor } from "@capacitor/core"
import { useSessionStore } from "@/stores/useSessionStore"
import { useTrackingStore } from "@/stores/useTrackingStore"

export class ApiTrackingDriver implements TrackingDriver {
  http: Axios
  hasStarted: boolean
  queue: { event: string, data: object }[]
  shouldTrack: boolean

  constructor(http: Axios) {
    this.http = http
    this.queue = []
  }

  async start(): Promise<void> {
    try {
      const response = await this.http.post("tracking/session")

      await this.setStoreFromResponse(response)

      this.shouldTrack = response.data.data.track || false

      this.hasStarted = true

      this.sendQueue()
    } catch (error) {
      console.debug(error)
    }
  }

  async send<T extends TrackingEvent>(
    event: TrackingEvent,
    payload: TrackingEventPayload[T],
  ): Promise<void> {
    const data = {
      event,
      data: {
        ...(useSessionStore().tracking),
        is_app: __CAPACITOR__ && Capacitor.isNativePlatform(),
        ...payload,
      },
    }

    if (!this.hasStarted) {
      this.queue.push(data)

      return
    }

    if (!this.shouldTrack) {
      return
    }

    try {
      await this.http.post("tracking/event", data)
    } catch (error) {
      console.debug(error)
    }
  }

  private sendQueue(): void {
    this.queue.forEach((data) => {
      try {
        this.http.post("tracking/event", data)
      } catch (error) {
        console.debug(error)
      }
    })
    this.queue = []
  }

  private setStoreFromResponse(response): void {
    const trackingStore = useTrackingStore()

    trackingStore.setDeviceToken(response.headers['x-tracking-device-token'])
    trackingStore.setSessionToken(response.headers['x-tracking-session-token'])
  }
}